import {ApacheContexts, Brands, Languages, Markets} from '@ox/typings';

export const apiUrlMap = {
  vehiclePreferences: {
    getItem: (vin: string, vehicleSettingId: string, loginId: string) => `/vehiclepreferences/vehicles/${vin}/settings/${vehicleSettingId}?loginId=${loginId}&includeDetails=false'`,
    list: (vin: string, loginId: string) => `/vehiclepreferences/vehicles/${vin}/settings?loginId=${loginId}'`,
    saveItem: (vin: string, loginId: string) => `/vehiclepreferences/vehicles/${vin}/settings?loginId=${loginId}`
  },
  profile: {
    person: '/profile/person/',
    getOwnerProfile: '/init/loginSuccessData',
    setLastActiveVehicle: (profileId: string) => `/${profileId}/garage/vehicles/lastActiveVehicle`,
    get: (id: string) => `/profile/${id}/person/`
  },
  garage: {
    getVehicles: '/garage/vehicles',
    updateMileage: (profileId: string) => `/${profileId}/garage/vehicle/mileage`,
    removeVehicle: (profileId: string) => `/${profileId}/garage/vehicle`,
    removeInactiveVehicle: '/oc-account-service/onstar/offboard',
    updateVehicle: (profileId: string, bypassVinSearch: boolean) => `/${profileId}/garage/vehicle?override=${bypassVinSearch}`,
    addVehicle: (profileId: string) => `/${profileId}/garage/vehicles`
  },
  generic: {
    getMmy: (vin: string) => `/generic/vin/${vin}/`
  },
  password: {
    put: (id: string) => `/profile/${id}/password/`
  },
  cards: {
    get: (id: string) => `/profile/${id}/cards/`,
    pots: (id: string) => `/profile/${id}/cards/`,
    delete: (id: string, creditCardLastFour: string) => `/profile/${id}/cards/${creditCardLastFour}`
  },
  cief: {
    onboarding: '/ceif/onboardingdata'
  },
  vehicle: {
    active: {
      mileage: (ownerProfileId: string, vehicleId: string, onstarAccount: string, vin: string, fuelType: string) => `/${ownerProfileId}/${vehicleId}/${onstarAccount}/${vin}/${fuelType}/activeVehicleMileage`
    }
  },
  ossSchedule: {
    upcoming: (market: string, gcin: string, today: string) => `/vehicleoss/oss/${market}/customers/gcin/${gcin}/appointments?startDate=${today}&format=json&dealerDetails=`,
    cancel: (market: string, bac: string, recordLocator: string) => `/vehicleoss/oss/${market}/dealerships/${bac}/appointments/${recordLocator}/status/cancelled`
  },
  entitlementsDataService: {
    GET: (vin: string) => `/onstar/entitlements/list/${vin}`,
    DRIVERTYPE: (vin: string) => `/onstar/entitlements/driverType/${vin}`
  },
  dealer: {
    locateByZip: (brand: string, model: string, zip: string) => `/generic/locateDealerBrandZip/${brand}/${model}/${zip}`,
    locateByCityState: (brand: string, model: string, city: string, state: string) => `/generic/locateDealerBrandCityState/${brand}/${model}/${city}/${state}`,
    locateByCity: (brand: string, model: string, city: string) => `/generic/locateDealerBrandCity/${brand}/${model}/${city}`,
    getPreferredDealer: (profileId: string) => `/profile/${profileId}/preferreddealer`,
    setPreferredDealer: (profileId: string, profileLastName: string, vehicleId: string, onstarAccountNumber: string, vehicleVin: string) => `/profile/${profileId}/preferreddealer?lastName=${profileLastName}&vehicleId=${vehicleId}&onStarAccount=${onstarAccountNumber}&vin=${vehicleVin}`,
    locateByDealerName: (brand: string, name: string) => `/generic/locateDealerByName/${brand}/${name}`,
    locateDealerLatLong: (brand: string, lat: string, long: string) => `/generic/locateDealerLatLong/${brand}/${lat}/${long}`
  },
  recallsGfas: {
    get: (vin: string) => (`/${vin}/gfas`)
  },
  warranties: {
    get: (vin: string) => (`/vin/${vin}/warranties`),
    getWarranties: (profileId: string | undefined, vin: string) => (`/${profileId}/vin/${vin}/warranties`)
  },
  recallCampaignVin: {
    decryptVin: '/recall/campaign/decrypt/vin'
  },
  takataSignUp: {
    saveTakataRecallData: '/recalls/takata/signupTakataRecall',
    signupForSMS: '/recalls/takata/signupForSMS'
  },
  marketingContent: {
    baseUrl: '/api/marketingContent',
    url: '/api/onStar/shop/offers/shop'
  },
  myPlansUnified: {
    copy: '/onStar/shop/copy',
    updatePlans: '/onStar/shop/updatePlans',
    subscriptions: '/onStar/shop/subscriptions',
    recommended: '/onStar/shop/recommended',
    expiryStatus: '/onstar/shop/expiryStatusPresented',
    renew: '/onStar/shop/renew',
    serviceUsage: '/onStar/shop/serviceUsage'
  },
  myPlansDigital: {
    orders: (vin: string) => `/api/vehicle/${vin}/orders`,
    products: () => '/api/products'
  }
  ,
  fmLandingPage: {
    inviteUrl: '/fme/emailInvite/?inviteId=',
    redirectUrl: '/fme/redirecturl/',
    processIdUrl: '/fme/redirecturl/'
  },
  aemContentStructure: {
    getDlpJSON: (brand?: string, region?: string, lang?: string, variation?: string, year?: string) => {
      const langPath = lang ? `${lang}/` : '';
      const variationPath = variation ? `_${variation}` : '';
      const yearPath = year ? `_${year}` : '';
      return `/content/dam/gmownercenter/gmna/digitalshop/${region}/${langPath}${brand}/dlp/layout/dlp_${brand}${variationPath}${yearPath}.json`;
    },
    getPdpJSON: (brand: string, product: string, region: string, lang?: string, variation?: string, year = '') => {
      const variationPath = variation ? `_${variation}` : '';
      const langPath = lang ? `${lang}/` : '';
      const yearPath = year ? `_${year}` : '';
      return `/content/dam/gmownercenter/gmna/digitalshop/${region}/${langPath}${brand}/pdp/${product}/${product}_${brand}${yearPath}${variationPath}.json`;
    },
    getConsentsJSON: (region: Markets, lang: Languages) => {
      return `/content/dam/gmownercenter/gmna/digitalshop/consents/${region}/${lang}/consents.json`;
    },
    getCountryValidationJSON: (region: Markets, lang: Languages) => {
      return `/content/dam/gmownercenter/gmna/digitalshop/countryValidation/${region}/${lang}/countryValidation.json`;
    },
    getDisclosuresJSON: (region: Markets, lang: Languages) => {
      return `/content/dam/gmownercenter/gmna/digitalshop/disclosures/${region}/${lang}/disclosures.json`;
    }
  },
  dlpContentStructure: {
    getDLPOffers: () => ('/oc-shop-service/bff/offers')
  },
  mmy: {
    YmmUrl: './getYMMs'
  },
  zuora: {
    zuoraEndpoint: '/zuora/signature/',
    savePayment: '/oc-shop-service/bff/associatePayment/v2'
  },
  cms: {
    bml: (bmlId: string | number, context: ApacheContexts) => `${context}cms/read/all/bmlId/${bmlId}`,
    missing: (brand: string, market: string, lang: string, key: string, context: ApacheContexts) => `${context}cms/read/${brand}/${market}/${lang}/?cmsKey=${key}`
  },
  featureFlags: {
    getAll: '/oc-global-services/flag-service/allFlagsState',
    get: (id: string) => `/oc-global-services/flag-service/flagState/${id}`,
    trackVariation: '/oc-global-services/flag-service/track',
    getVariation: (id: string) => `/oc-global-services/flag-service/variation/${id}`
  },
  paypal: {
    paypalEndpoint: '/pm/paypal/redirect'
  },
  placeOrder: {
    orderApiEndPoint: '/onStar/shop/order',
    statusApiEndPoint: '/onStar/shop/order/status',
    transactionApiEndPoint: '/onStar/transaction'
  },
  review: {
    getReviewPlanInfo: '/onStar/shop/review'
  },
  content: {
    assets: (market: Markets, lang: Languages, assets: string): string => {
      const urlPath = (assets === 'true')
        ? `/content/dam/gmownercenter/gmna/digitalshop/assets-2024/${market}/${lang}/assets.json`
        : `/content/dam/gmownercenter/gmna/digitalshop/assets/${market}/${lang}/assets.json`;
      return urlPath;
    }
  },
  shop: {
    quote: '/oc-shop-service/bff/quote/v2',
    copy: (brand: Brands, market: Markets) => `/oc-shop-service/bff/copy/v2?country=${market}&make=${brand}`,
    recurringCharges: '/oc-shop-service/bff/recurringCharges/v2',
    cancelPlans: '/oc-shop-service/bff/cancelPlans/v2',
    authRecommendedProducts: '/ownercenter/b2c/api/oc-shop-service/bff/recommendedProducts/v2',
    unauthRecommendedProducts: '/ownercenter/b2c/api/oc-shop-service/bff/retail/recommendedProducts/v2',
    ymalAssets: (market: Markets, lang: Languages, context: ApacheContexts) => `${context}content/dam/gmownercenter/gmna/digitalshop/ymal/ymal_assets_${market}_${lang}.json`,
    authComparePlan: '/oc-shop-service/bff/comparePlans/v2',
    unauthComparePlan: '/oc-shop-service/bff/retail/comparePlans/v2',
    unAuthPricing: '/oc-shop-service/bff/retail/pricing/v2',
    authPricing: '/oc-shop-service/bff/pricing/v2',
    unAuthOffers: '/oc-shop-service/bff/retail/offers/v2',
    authOffers: '/oc-shop-service/bff/offers/v2'
  },
  myPlans: {
    getMyPlans: '/oc-shop-service/bff/myPlans/v2'
  },
  loginRegister: {
    lookup: (type: string, attempt: string, token: string) => `/ceif/${type}/${attempt}/${token}`
  }
} as const;
